import React from 'react'
import { Box, FormControl, FormLabel, HStack, Switch, Text, Tooltip, VStack } from '@chakra-ui/react'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { BRAND_HEX } from './nodeStyle'

interface LinkGraphLegendProps {
  truncated: boolean
  hitCap: boolean
  showSuggestions: boolean
  /** Suggestions need embeddings; when they are not ready the switch is disabled. */
  suggestionsAvailable: boolean
  onToggleSuggestions: (show: boolean) => void
}

export default function LinkGraphLegend({
  truncated,
  hitCap,
  showSuggestions,
  suggestionsAvailable,
  onToggleSuggestions,
}: LinkGraphLegendProps) {
  const { t } = useAppTranslation('files')

  return (
    <Box
      position="absolute"
      left={3}
      top={3}
      zIndex={5}
      bg="whiteAlpha.900"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      px={3}
      py={2}
      fontSize="xs"
      color="gray.700"
      textAlign="left"
    >
      <VStack align="stretch" spacing={1}>
        <HStack spacing={2}>
          <Box w="22px" h="0" borderTopWidth="2px" borderColor="gray.400" position="relative">
            <Box
              position="absolute"
              right="-1px"
              top="-4px"
              w={0}
              h={0}
              borderTop="3px solid transparent"
              borderBottom="3px solid transparent"
              borderLeft="6px solid"
              borderLeftColor="gray.400"
            />
          </Box>
          <Text>{t('filePreview.linkGraph.legendLink')}</Text>
        </HStack>
        <HStack spacing={2}>
          <Box w="22px" display="flex" justifyContent="center">
            <Box w="10px" h="10px" borderRadius="full" bg="gray.400" borderWidth="2px" borderColor={BRAND_HEX} />
          </Box>
          <Text>{t('filePreview.linkGraph.legendCenter')}</Text>
        </HStack>
        <Tooltip
          label={t('filePreview.linkGraph.suggestionsUnavailable')}
          isDisabled={suggestionsAvailable}
          placement="right"
          hasArrow
        >
          <FormControl display="flex" alignItems="center" isDisabled={!suggestionsAvailable} w="auto">
            <Box w="22px" h="0" borderTopWidth="2px" borderStyle="dashed" borderColor="blue.300" mr={2} />
            <Switch
              id="link-graph-suggestions"
              size="sm"
              colorScheme="brand"
              isChecked={showSuggestions}
              onChange={(e) => onToggleSuggestions(e.target.checked)}
            />
            <FormLabel htmlFor="link-graph-suggestions" mb={0} ml={2} fontSize="xs" fontWeight="normal" cursor="pointer">
              {t('filePreview.linkGraph.showSuggestions')}
            </FormLabel>
          </FormControl>
        </Tooltip>
        <Text color="gray.500" pt={1}>
          {t('filePreview.linkGraph.hint')}
        </Text>
        {(truncated || hitCap) && (
          <Text color="orange.600" pt={1}>
            {hitCap ? t('filePreview.linkGraph.limitReached') : t('filePreview.linkGraph.truncated')}
          </Text>
        )}
      </VStack>
    </Box>
  )
}
